'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import Image from 'next/image';
import { ArrowLeft, Loader2, Package, Plus, Search, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NewProductAIForm } from './new-product-ai-form';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PRODUCT_CATEGORIES } from '@/lib/constants';
import { formatCategory } from '@/lib/utils';
import { marketplaceApi } from '@/lib/api';
import { useCurrentBusiness } from '@/lib/business-context';
import { useAuthModal } from '@/components/auth/auth-modal';
import { toCityKey } from '@/lib/city-key';

const MARKETPLACE_REFRESH_EVENT = 'marketplace:refresh';





interface AddPostModalContextValue {
  openAddPostModal: () => void;
  closeAddPostModal: () => void;
}

const AddPostModalContext = createContext<AddPostModalContextValue>({
  openAddPostModal: () => {},
  closeAddPostModal: () => {},
});

export function useAddPostModal() {
  return useContext(AddPostModalContext);
}

export function AddPostModalProvider({ children }: { children: ReactNode }) {
  const { currentBusiness, businesses } = useCurrentBusiness();
  const { openAuthModal } = useAuthModal();

  const [open, setOpen] = useState(false);
  const [postType, setPostType] = useState<'buy' | 'sell' | null>(null);
  const [sellStep, setSellStep] = useState<'pick' | 'new'>('pick');
  const [myProducts, setMyProducts] = useState<any[]>([]);
  const [myProductsLoading, setMyProductsLoading] = useState(false);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const [postForm, setPostForm] = useState({
    title: '',
    details: '',
    category: '',
    budgetMax: '',
    cityKey: '',
  });

  
  const biz = currentBusiness ?? businesses?.[0];
  const defaultCityKey = biz
    ? toCityKey((biz as any).locationCountry, (biz as any).locationRegion, (biz as any).locationCity)
    : 'us_ca_los_angeles';
  const isGuest = !currentBusiness && businesses.length === 0;

  const notifyMarketplaceRefresh = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(MARKETPLACE_REFRESH_EVENT));
  }, []);

  const resetModalState = useCallback(() => {
    setPostType(null);
    setSellStep('pick');
    setPostSubmitting(false);
    setPostForm({
      title: '',
      details: '',
      category: '',
      budgetMax: '',
      cityKey: '',
    });
  }, []);

  const fetchMyProducts = useCallback(async () => {
    setMyProductsLoading(true);
    try {
      const data = await marketplaceApi.getProducts(true);
      setMyProducts(Array.isArray(data) ? data : []);
    } catch {
      setMyProducts([]);
    } finally {
      setMyProductsLoading(false);
    }
  }, []);

  const closeAddPostModal = useCallback(() => {
    setOpen(false);
    resetModalState();
  }, [resetModalState]);

  const openAddPostModal = useCallback(() => {
    if (isGuest) {
      openAuthModal('signup', '/marketplace');
      return;
    }
    resetModalState();
    setOpen(true);
  }, [isGuest, openAuthModal, resetModalState]);

  const handleCreateBuyPost = useCallback(async () => {
    if (!postForm.title.trim() || postSubmitting) return;

    setPostSubmitting(true);
    try {
      await marketplaceApi.createBuyPost({
        title: postForm.title.trim(),
        details: postForm.details.trim() || undefined,
        category: postForm.category.trim() || undefined,
        budgetMax: postForm.budgetMax ? Number(postForm.budgetMax) : undefined,
        cityKey: postForm.cityKey || defaultCityKey,
      });
      toast.success('Buy post created');
      closeAddPostModal();
      notifyMarketplaceRefresh();
    } catch {
      toast.error('Failed to create post');
    } finally {
      setPostSubmitting(false);
    }
  }, [closeAddPostModal, defaultCityKey, notifyMarketplaceRefresh, postForm, postSubmitting]);

  const handleCreateSellProduct = useCallback(async (data: {
    productType: 'goods' | 'service';
    title: string;
    description?: string;
    ingredients: string[];
    allergies: string[];
    unit: string;
    price: number;
    minQty: number;
    category?: string;
    imageFile?: File;
  }) => {
    setPostSubmitting(true);
    try {
      const { imageFile, ...productPayload } = data;
      const newProduct = await marketplaceApi.createProduct({
        ...productPayload,
        cityKey: defaultCityKey,
        isActive: true,
      });
      if (imageFile && newProduct?.id) {
        try {
          await marketplaceApi.uploadImage(newProduct.id, imageFile);
        } catch {
          toast.error('Product created but image upload failed');
        }
      }
      toast.success('Product listed on marketplace');
      closeAddPostModal();
      notifyMarketplaceRefresh();
    } catch {
      toast.error('Failed to create post');
    } finally {
      setPostSubmitting(false);
    }
  }, [closeAddPostModal, defaultCityKey, notifyMarketplaceRefresh]);

  const contextValue = useMemo<AddPostModalContextValue>(
    () => ({ openAddPostModal, closeAddPostModal }),
    [closeAddPostModal, openAddPostModal],
  );

  return (
    <AddPostModalContext.Provider value={contextValue}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) resetModalState();
        }}
      >
        <DialogContent className="max-w-md">
          {!postType ? (
            <>
              <DialogHeader>
                <DialogTitle>Add Post</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <button
                  onClick={() => {
                    setPostType('buy');
                    setPostForm((current) => ({ ...current, cityKey: defaultCityKey }));
                  }}
                  className="flex flex-col items-center gap-3 rounded-xl border-2 border-transparent bg-muted/50 p-6 transition-all hover:border-primary hover:bg-primary/5"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold">Buy</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Looking for something</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setPostType('sell');
                    setSellStep('pick');
                                    setPostForm((current) => ({ ...current, cityKey: defaultCityKey }));
                    void fetchMyProducts();
                  }}
                  className="flex flex-col items-center gap-3 rounded-xl border-2 border-transparent bg-muted/50 p-6 transition-all hover:border-primary hover:bg-primary/5"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <Store className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold">Sell</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">List a product</p>
                  </div>
                </button>
              </div>
            </>
          ) : postType === 'sell' && sellStep === 'pick' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <button
                    onClick={() => setPostType(null)}
                    className="rounded-md p-1 transition-colors hover:bg-muted"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  What are you selling?
                </DialogTitle>
              </DialogHeader>
              <div className="max-h-[360px] space-y-2 overflow-y-auto py-1">
                {myProductsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {myProducts.map((product) => (
                      <button
                        key={product.id}
                        onClick={async () => {
                          setPostSubmitting(true);
                          try {
                            if (!product.isActive) {
                              await marketplaceApi.updateProduct(product.id, {
                                isActive: true,
                                cityKey: defaultCityKey,
                              });
                            } else if (!product.cityKey) {
                              await marketplaceApi.updateProduct(product.id, { cityKey: defaultCityKey });
                            }
                            toast.success(`${product.title} listed on marketplace`);
                            closeAddPostModal();
                            notifyMarketplaceRefresh();
                          } catch {
                            toast.error('Failed to list product');
                          } finally {
                            setPostSubmitting(false);
                          }
                        }}
                        disabled={postSubmitting}
                        className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
                      >
                        {product.imageUrl ? (
                          <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md">
                            <Image src={product.imageUrl} alt="" fill sizes="40px" className="object-cover" />
                          </div>
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{product.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {product.price != null ? `$${Number(product.price).toFixed(2)}` : 'No price'}
                            {product.unit && product.unit !== 'each' ? ` / ${product.unit}` : ''}
                            {product.category ? ` · ${formatCategory(product.category)}` : ''}
                          </p>
                        </div>
                        {product.isActive && (
                          <Badge variant="secondary" className="shrink-0 text-[10px]">
                            Active
                          </Badge>
                        )}
                      </button>
                    ))}
                    <button
                      onClick={() => setSellStep('new')}
                      className="flex w-full items-center gap-3 rounded-lg border border-dashed p-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
                        <Plus className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Create new product</p>
                        <p className="text-xs text-muted-foreground">Add a brand new listing</p>
                      </div>
                    </button>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              {postType === 'sell' ? (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <button
                        onClick={() => setSellStep('pick')}
                        className="rounded-md p-1 transition-colors hover:bg-muted"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      New product
                    </DialogTitle>
                  </DialogHeader>
                  <div className="max-h-[70vh] overflow-y-auto pr-1">
                    <NewProductAIForm
                      submitLabel="Post"
                      loadingLabel="Posting..."
                      onCancel={() => setSellStep('pick')}
                      onSubmit={handleCreateSellProduct}
                    />
                  </div>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <button
                        onClick={() => setPostType(null)}
                        className="rounded-md p-1 transition-colors hover:bg-muted"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                      What do you need?
                    </DialogTitle>
                  </DialogHeader>
                  <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={postForm.title}
                        onChange={(event) => setPostForm((current) => ({ ...current, title: event.target.value }))}
                        placeholder="e.g. Organic flour 50lb"
                      />
                    </div>
                    <div>
                      <Label>Details</Label>
                      <Input
                        value={postForm.details}
                        onChange={(event) =>
                          setPostForm((current) => ({ ...current, details: event.target.value }))
                        }
                        placeholder="Optional"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Select
                          value={postForm.category || '_none'}
                          onValueChange={(value) =>
                            setPostForm((current) => ({ ...current, category: value === '_none' ? '' : value }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">None</SelectItem>
                            {PRODUCT_CATEGORIES.map((category) => (
                              <SelectItem key={category.value} value={category.value}>
                                {category.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Max Budget ($)</Label>
                        <Input
                          type="number"
                          min={0}
                          value={postForm.budgetMax}
                          onChange={(event) =>
                            setPostForm((current) => ({ ...current, budgetMax: event.target.value }))
                          }
                          placeholder="Optional"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={closeAddPostModal}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateBuyPost} disabled={!postForm.title.trim() || postSubmitting}>
                      {postSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post'}
                    </Button>
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AddPostModalContext.Provider>
  );
}
